import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import { LOCAL_STORAGE, QUERY_KEYS, ROLES, ROLE_NAME, ROUTES } from '../../../constants';
import { Header, SideBar, SubscriptionExpiryPopup } from '../../shared';
import ContentBox from './ContentBox';
import {
    setAccounts,
    setSelectedAccountName,
    setSelectedAccounts,
    setUserData,
} from '../../../redux';
import { getLocalStorageItem, getUserById, popup, setLocalStorageItem } from '../../../utils';
import { useQuery } from '@tanstack/react-query';

export const CustomerDashboard = () => {
    const location = useLocation();
    const pathname = useMemo(
        () => location?.pathname || ROUTES.ADMIN.DASHBOARD,
        [location.pathname],
    );
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const userID = getLocalStorageItem(LOCAL_STORAGE.USER_ID);
    const isImpersonated = getLocalStorageItem(LOCAL_STORAGE.IS_IMPERSONATING);
    const userRole = useSelector((s) => s.user.role);
    const role = useMemo(() => userRole || searchParams.get('role'), [searchParams, userRole]);

    const { accounts, selectedAccount: selectedAccountInDropDown } = useSelector(
        (state) => state.accountArray,
    );
    const [currentDashboardRoute, setCurrentDashboardRoute] = useState(pathname);
    const [selectedAccount, setSelectedAccount] = useState(selectedAccountInDropDown);

    const handleNewAccountSelection = (accountArray, selectedAccountId) => {
        if (selectedAccountId) {
            const previouslySelectedAccount = accountArray.find(
                (account) => account.id === selectedAccountId,
            );
            if (!previouslySelectedAccount) setSelectedAccount(accountArray[0].id);
            else {
                const isUserPrimary = previouslySelectedAccount.is_primary_user;
                setLocalStorageItem(
                    LOCAL_STORAGE.ROLE_NAME,
                    isUserPrimary ? ROLE_NAME.PRIMARY_USER : ROLE_NAME.SECONDARY_USER,
                );
                setLocalStorageItem(
                    LOCAL_STORAGE.ROLE_CODE,
                    isUserPrimary ? ROLES.PRIMARY_USER : ROLES.SECONDARY_USER,
                );
                dispatch(setSelectedAccountName(selectedAccountId));
            }
        } else {
            setSelectedAccount(accountArray[0].id);
        }
    };

    const userQuery = useQuery({
        queryKey: [QUERY_KEYS.USER_BY_ID, userID],
        queryFn: () => getUserById({ id: userID }),
        enabled: !!userID,
        select: (res) => res?.data,
    });
    if (userQuery.isError) popup('error', userQuery.error.message);

    useEffect(() => {
        if (userQuery.data && !isEmpty(userQuery.data.accounts)) {
            const data = userQuery.data;
            dispatch(setAccounts(data.accounts));
            setLocalStorageItem(LOCAL_STORAGE.FULL_NAME, data.full_name);
            setLocalStorageItem(LOCAL_STORAGE.EMAIL, data.email);
            setLocalStorageItem(LOCAL_STORAGE.ACCOUNT_ID, data?.accounts[0]?.id);
            dispatch(
                setUserData({
                    firstName: data?.first_name,
                    lastName: data?.last_name,
                    imagePath: data?.image || null,
                    role: data?.role_code,
                }),
            );
            handleNewAccountSelection(data?.accounts, selectedAccountInDropDown);
            if (searchParams.get('account_id') && !isEmpty(accounts)) {
                dispatch(setSelectedAccounts(searchParams.get('account_id')));
                searchParams.delete('account_id');
                setSearchParams(searchParams);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userQuery.data]);

    useEffect(() => {
        if (searchParams.get(LOCAL_STORAGE.IMPERSONATE_TOKEN)) {
            dispatch(setUserData({ role: searchParams.get('role') }));
            setLocalStorageItem(LOCAL_STORAGE.LOGIN_AT, Date.now());
            setLocalStorageItem(
                LOCAL_STORAGE.AUTH_TOKEN,
                searchParams.get(LOCAL_STORAGE.IMPERSONATE_TOKEN),
            );
            setLocalStorageItem(
                LOCAL_STORAGE.REFRESH_TOKEN,
                searchParams.get(LOCAL_STORAGE.REFRESH_TOKEN),
            );
            setLocalStorageItem(LOCAL_STORAGE.USER_ID, searchParams.get(LOCAL_STORAGE.USER_ID));
            setLocalStorageItem(LOCAL_STORAGE.IS_IMPERSONATING, true);
            setSelectedAccount(searchParams.get('account_id'));
            setSearchParams({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        if (!isEmpty(selectedAccount)) {
            dispatch(setSelectedAccounts(selectedAccount));
        } else if (accounts.length > 0 && searchParams.has('account_id')) {
            setSelectedAccount(searchParams.get('account_id'));
        } else if (accounts.length > 0 && !searchParams.has('account_id')) {
            setSelectedAccount(accounts[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccount]);

    useEffect(() => {
        if (!role) {
            navigate(ROUTES.CUSTOMER.LOGIN, { replace: true });
        }
        // redirect /customer to /customer/* if already logged in
        if (pathname === ROUTES.CUSTOMER.DEFAULT && role) {
            navigate(ROUTES.CUSTOMER.DASHBOARD, { replace: true });
        }
        setCurrentDashboardRoute(pathname);
    }, [role, pathname, navigate]);

    // Don’t render anything until auth check is resolved
    if (!role) return null;

    return (
        <>
            <Header
                isAdmin={false}
                accounts={accounts}
                selectedAccount={selectedAccount}
                setSelectedAccount={setSelectedAccount}
                showCustomerDropdown={true}
            />
            <SubscriptionExpiryPopup />
            <main className={`${isImpersonated && 'isImpersonated'}`}>
                <div className='w-100 h-100 row mx-0 position-relative'>
                    <SideBar
                        sidebarFor='client'
                        currentDashboardRoute={currentDashboardRoute}
                        setCurrentDashboardRoute={setCurrentDashboardRoute}
                    />
                    <ContentBox
                        currentRoute={currentDashboardRoute}
                        pathname={pathname}
                        isImpersonated={isImpersonated}
                    />
                </div>
            </main>
        </>
    );
};
